/**
 * ──────────────────────────────────────────────────────────────────────────
 *  CONTEXT-LOCKED PALETTE — CAP weather-hazard family (operator-cop-doctrine
 *  §3A.7, §8.4 decision a, 2026-06-03).
 *
 *  This module is the SINGLE source of the CAP extreme-weather hazard palette.
 *  It is consumed ONLY by the weather-polygon family (ExtremeWeatherWarningsLayer
 *  — the CAP half of the Anomaly Queue / Urgent Anomalies surface).
 *
 *  Palette (locked law — do NOT re-tune the hex values here):
 *    tornado/waterspout magenta-red #b91c5c (Colour Law 5 — no CAP polygon
 *    impersonates the urgent-anomaly red #ff3b30) · hurricane/tropical purple
 *    #9333ea · freezing-rain/ice-storm blue #3b82f6 · blizzard/snow light-blue
 *    #0ea5e9 · flood dark-blue #1e40af · extreme-heat orange #ea580c
 *    · severe-storm/wind amber #f59e0b · other grey #6b7280
 *
 *  Colour Law 4/5: every fill alpha clamps to ≤0.18 and every stroke to ≤0.85 so
 *  a CAP polygon stays an ambient field, never an act-register block.
 *
 *  Overlap debt this lock resolves (§3A.7): hues that mean something else in
 *  OTHER families must never leak across the boundary —
 *    - blue (#3b82f6 freezing rain) also means L2 response and UC fire
 *    - amber (#f59e0b severe storm) also means L3 response and OC-Monitored fire
 *
 *  ⛔ Do NOT import from ./responseLevel or ./fireStages (enforced by the
 *     no-restricted-imports lint rule in eslint.config.js). Color is never
 *     load-bearing alone: every polygon's hazard is paired with the CAP event
 *     name in the side panel (ExtremeWeatherBody). The Extreme-severity breathing
 *     outline is REVOKED (Colour Law 6 motion budget — the only map motion is the
 *     urgent-anomaly pulse + the live-camera breathe); CAP outlines are static.
 * ──────────────────────────────────────────────────────────────────────────
 *
 * Maps a CAP `event` keyword to a fill colour + opacity so the operator can read
 * the hazard type at a glance on the blended Urgent Anomalies surface. Pure +
 * exported so the keyword→colour table is unit-testable without Cesium.
 *
 * Matching is substring-on-lowercased-event (CAP events are free text, e.g.
 * "Tornado Warning", "Severe Thunderstorm Watch"), most-specific first. Anything
 * Severe/Extreme that doesn't match a keyword falls through to grey (never
 * dropped — an unrecognised warning still renders).
 *
 * #305 (CAHS Phase 3, Wave 1): each rule ALSO carries the CAHS clean-room kind
 * glyph for that hazard, so the colour AND the glyph come from this one table —
 * the keyword→colour and keyword→glyph mappings can never drift apart. The glyph
 * is the SHAPE channel only (PART 2.2: hazards take the glyph, NOT the diamond
 * frame); colour stays the Law-4/5 ambient fill law here, untouched.
 */

import type { CahsGlyphKey } from '../assets/cahs/index.generated';

export interface ExtremeWeatherStyle {
  /** CSS hex fill colour. */
  color: string;
  /** Fill opacity 0–1. */
  alpha: number;
  /** Stroke (outline) opacity — bolder than the fill for legibility. */
  strokeAlpha: number;
}

interface Rule {
  /** Lowercased substrings; any match selects this rule. */
  keywords: string[];
  style: ExtremeWeatherStyle;
  /** The CAHS clean-room kind glyph for this hazard (#305). Pairs with `style`
   *  so colour + glyph stay in lockstep — one rule, one hazard. */
  glyph: CahsGlyphKey;
}

/** Ordered most-specific → least; first keyword hit wins. Colour Law 4/5: fills
 *  clamp to ≤0.18 (ambient register), strokes to ≤0.85, and no hue uses the
 *  saturated red the urgent anomaly owns. */
const RULES: Rule[] = [
  // evacuation ORDER (protective action, e.g. "Wildfire — Evacuation Order") → the CAHS
  // `evacuation` glyph. MOST-SPECIFIC-FIRST so an evac order that also names its hazard
  // ("Wildfire — Evacuation Order") reads as the ORDER ('who must move'), matching the
  // DB RL-gate's evacuation-before-wildfire intent (0098) and the violet protective-action
  // register the operator/GeoBC evac layers use. Icon one-swing 2026-07-06 (#564): this
  // rule EXISTED in the DB RL-gate but never in the client glyph table, so an Elliot-Lake
  // "Wildfire — Evacuation Order (illustrative)" CAP took the RL4 urgent branch yet
  // capEventGlyph() returned null → the marker rendered SYMBOL-LESS (the founder bug). The
  // fill stays the ambient grey DEFAULT (no new CAP polygon colour / no Law-2 distinctness
  // surface) — the URGENCY is carried by the RESPONSE-LEVEL-filled disc POINT marker, not
  // the ambient area field; only the missing kind-glyph is restored here.
  { keywords: ['evacuation', 'evacuate'], style: { color: '#6b7280', alpha: 0.18, strokeAlpha: 0.7 }, glyph: 'evacuation' },
  // tornado / waterspout → magenta-red (Law 5: no CAP polygon impersonates URGENT-RED)
  { keywords: ['tornado', 'waterspout'], style: { color: '#b91c5c', alpha: 0.18, strokeAlpha: 0.85 }, glyph: 'tornado' },
  // hurricane / tropical storm → purple
  { keywords: ['hurricane', 'tropical storm'], style: { color: '#9333ea', alpha: 0.18, strokeAlpha: 0.85 }, glyph: 'hurricane' },
  // freezing rain / freezing drizzle / ice storm → blue
  {
    keywords: ['freezing rain', 'freezing drizzle', 'ice storm'],
    style: { color: '#3b82f6', alpha: 0.18, strokeAlpha: 0.8 },
    glyph: 'ice-storm',
  },
  // blizzard / heavy snow / snow squall → light-blue
  {
    keywords: ['blizzard', 'heavy snow', 'snow squall'],
    style: { color: '#0ea5e9', alpha: 0.18, strokeAlpha: 0.8 },
    glyph: 'blizzard',
  },
  // flood / flash flood → dark-blue
  { keywords: ['flash flood', 'flood'], style: { color: '#1e40af', alpha: 0.18, strokeAlpha: 0.8 }, glyph: 'flood' },
  // extreme heat / heat warning → orange
  { keywords: ['extreme heat', 'heat warning', 'heat'], style: { color: '#ea580c', alpha: 0.18, strokeAlpha: 0.8 }, glyph: 'extreme-heat' },
  // severe thunderstorm / squall / wind → amber
  {
    keywords: ['severe thunderstorm', 'thunderstorm', 'squall', 'wind'],
    style: { color: '#f59e0b', alpha: 0.18, strokeAlpha: 0.8 },
    glyph: 'severe-storm',
  },
];

/** Default (other Severe/Extreme) → grey. */
export const DEFAULT_EXTREME_WEATHER_STYLE: ExtremeWeatherStyle = {
  color: '#6b7280',
  alpha: 0.18,
  strokeAlpha: 0.7,
};

/** Resolve the fill style for a CAP event name (null/empty → grey default). */
export function extremeWeatherStyle(event: string | null | undefined): ExtremeWeatherStyle {
  if (!event) return DEFAULT_EXTREME_WEATHER_STYLE;
  const e = event.toLowerCase();
  for (const rule of RULES) {
    if (rule.keywords.some((k) => e.includes(k))) return rule.style;
  }
  return DEFAULT_EXTREME_WEATHER_STYLE;
}

/**
 * Resolve the CAHS clean-room kind glyph for a CAP event name (#305). Same
 * keyword table as {@link extremeWeatherStyle}, so the glyph and the fill colour
 * always agree. Returns `null` for the grey default (an unrecognised Severe/
 * Extreme warning keeps its colour + event-name label, but carries no kind glyph
 * rather than a misleading one).
 *
 * For the AMBIENT register (Severe, or any acknowledged alert) the caller paints
 * the glyph with the lifecycle token (URGENT-RED → ACK-STEEL) and the glyph IS the
 * CAP point marker (founder ruling 2026-06-16: no separate anomaly dot; the red
 * kind-glyph is the point). #359 carves one exception: an UNACKED RL-qualifying
 * urgent (Extreme severity) instead rides as a DARK-KNOCKOUT glyph inside the
 * pulsing URGENT-RED anomaly dot (the fused-hotspot compositing) — so for that
 * path the glyph is NOT the lifecycle token and there IS a backing dot. See
 * ExtremeWeatherWarningsLayer.renderGlyphs + the greenMotion.ts amendment.
 */
export function capEventGlyph(event: string | null | undefined): CahsGlyphKey | null {
  if (!event) return null;
  const e = event.toLowerCase();
  for (const rule of RULES) {
    if (rule.keywords.some((k) => e.includes(k))) return rule.glyph;
  }
  return null;
}
